import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, CheckCircle2, Globe, Loader2, Mail, Sparkles, Unplug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PageHeader from "@/components/PageHeader";
import FacebookConnect from "@/components/FacebookConnect";
import HelperSettingsSection from "@/components/HelperSettingsSection";
import DatabaseSettingsSection from "@/components/DatabaseSettingsSection";
import BackupSettingsSection from "@/components/BackupSettingsSection";
import AutopilotSettingsSection from "@/components/AutopilotSettingsSection";
import BrandStyleSection from "@/components/BrandStyleSection";
import api, { errMsg } from "@/lib/api";

function Section({ icon: Icon, title, desc, children, id }) {
  return (
    <section className="panel fade-up p-6" data-testid={id}>
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary"><Icon className="h-4 w-4" /></div>
        <div><h2 className="font-bold">{title}</h2><p className="mt-1 text-sm text-muted-foreground">{desc}</p></div>
      </div>
      <div className="mt-6 space-y-4">{children}</div>
    </section>
  );
}

function PageSection({ s, setS }) {
  const [pageId, setPageId] = useState(s.page_id);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (r?.data) setS((p) => ({ ...p, ...r.data })); toast.success(ok(r?.data)); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => { const r = await api.put("/settings", { page_id: pageId, page_token: token || undefined }); setToken(""); return r; }, () => "Tetapan Page disimpan");
  const test = () => run("test", () => api.post("/settings/test-page"), (d) => `Bersambung ke: ${d.page_name}`);
  const disconnect = () => run("dc", async () => { const r = await api.delete("/settings/page"); setPageId(""); return r; }, () => "Page diputuskan");

  return (
    <Section icon={Globe} id="settings-page-section" title="Facebook Page (post automatik)" desc="Post ke Page anda dihantar sendiri oleh sistem pada masa dijadualkan.">
      <div className="flex items-center gap-2 text-sm" data-testid="settings-page-status">
        {s.page_connected ? <><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Disimpan {s.page_name && <>— <b>{s.page_name}</b></>} <span className="font-mono text-xs text-muted-foreground">{s.token_hint}</span></> : <span className="text-muted-foreground">Belum disambung</span>}
      </div>
      <FacebookConnect onConnected={() => api.get("/settings").then((r) => { setS(r.data); setPageId(r.data.page_id); })} />
      <p className="text-xs leading-relaxed text-muted-foreground">Tekan butang di atas → halaman log masuk Facebook dibuka → masukkan username & kata laluan Facebook anda di situ → benarkan akses Page. Kata laluan anda tidak disimpan oleh PostPintar.</p>
      <Accordion type="single" collapsible>
        <AccordionItem value="manual" className="border-border">
          <AccordionTrigger className="text-sm" data-testid="settings-manual-token-toggle">Atau tampal token secara manual</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-1">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Page ID</Label><Input value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="cth: 104857261234567" data-testid="settings-fb-page-id" /></div>
        <div className="space-y-2"><Label>Page Access Token</Label><Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={s.has_token ? "Biarkan kosong untuk kekal" : "EAAG..."} data-testid="settings-fb-access-token" /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={!!busy} data-testid="settings-save-page-btn">{busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan</Button>
      </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={test} disabled={!!busy || !s.page_connected} data-testid="settings-test-page-btn">{busy === "test" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Uji sambungan</Button>
        {s.page_connected && <Button variant="ghost" onClick={disconnect} disabled={!!busy} className="text-red-300" data-testid="settings-disconnect-page-btn"><Unplug className="mr-2 h-4 w-4" />Putuskan</Button>}
      </div>
      <Accordion type="single" collapsible>
        <AccordionItem value="guide" className="border-border">
          <AccordionTrigger className="text-sm" data-testid="settings-page-guide-toggle">Cara dapatkan Page ID & token (manual)</AccordionTrigger>
          <AccordionContent className="space-y-2 text-sm leading-relaxed text-muted-foreground">
            <p>1. Buka developers.facebook.com, cipta App (jenis Business).</p>
            <p>2. Buka Graph API Explorer, pilih App anda, tambah kebenaran <code>pages_manage_posts</code> dan <code>pages_read_engagement</code>, kemudian tekan Generate Access Token.</p>
            <p>3. Dalam “User or Page”, pilih Page anda — salin token tersebut. Untuk token tahan lama, tukar di Access Token Debugger → “Extend Access Token”.</p>
            <p>4. Page ID boleh dilihat di Page anda → About → Page transparency, atau melalui panggilan <code>me/accounts</code>.</p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Section>
  );
}

function ReminderSection({ s, setS }) {
  const [email, setEmail] = useState(s.reminder_email);
  const [busy, setBusy] = useState("");
  const [perm, setPerm] = useState("Notification" in window ? Notification.permission : "unsupported");

  const save = async (patch, msg) => {
    setBusy("save");
    try { const r = await api.put("/settings", patch); setS(r.data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const testEmail = async () => {
    setBusy("test");
    try { await api.post("/settings/test-email"); toast.success(`Emel ujian dihantar ke ${s.reminder_email}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const askPerm = async () => setPerm(await Notification.requestPermission());

  return (
    <Section icon={BellRing} id="settings-reminder-section" title="Peringatan" desc="Bila post untuk group tiba masanya, anda akan diberitahu supaya boleh post dengan satu klik.">
      <div className="space-y-2">
        <Label>Emel peringatan</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="anda@contoh.my" data-testid="settings-email-input" />
          <Button onClick={() => save({ reminder_email: email }, "Emel disimpan")} disabled={!!busy} data-testid="settings-save-email-btn">Simpan</Button>
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-border p-4">
        <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">Hantar emel peringatan</p><p className="text-xs text-muted-foreground">Emel sebenar dihantar setiap kali post group tiba masanya</p></div></div>
        <Switch checked={s.email_enabled} disabled={!s.reminder_email || !!busy} onCheckedChange={(v) => save({ email_enabled: v }, v ? "Emel peringatan dihidupkan" : "Emel peringatan dimatikan")} data-testid="settings-email-toggle" />
      </div>
      <Button variant="secondary" onClick={testEmail} disabled={!s.reminder_email || !!busy} data-testid="settings-test-email-btn">{busy === "test" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Hantar emel ujian</Button>
      <div className="flex items-center justify-between rounded-lg border border-border p-4">
        <div className="flex items-center gap-3"><BellRing className="h-4 w-4 text-muted-foreground" /><div><p className="text-sm font-medium">Notifikasi browser</p><p className="text-xs text-muted-foreground">Muncul walaupun tab lain dibuka (app perlu terbuka di browser)</p></div></div>
        {perm === "granted" ? <span className="text-xs text-emerald-400" data-testid="settings-browser-notif-status">Dibenarkan</span>
          : perm === "unsupported" ? <span className="text-xs text-muted-foreground">Tidak disokong</span>
          : <Button size="sm" variant="outline" onClick={askPerm} data-testid="settings-browser-notif-btn">Benarkan</Button>}
      </div>
    </Section>
  );
}

function AiSection({ s, setS }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try { const r = await api.put("/settings", { ai_best_time: v }); setS(r.data); toast.success(v ? "Cadangan masa terbaik AI dihidupkan" : "Cadangan masa terbaik AI dimatikan"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section icon={Sparkles} id="settings-ai-section" title="AI" desc="Kawal ciri AI yang membantu anda merancang post.">
      <div className="flex items-center justify-between rounded-lg border border-border p-4">
        <div><p className="text-sm font-medium">Cadangan masa terbaik</p><p className="text-xs text-muted-foreground">AI cadangkan hari & masa post untuk setiap group berdasarkan rekod post yang selesai</p></div>
        <Switch checked={s.ai_best_time} disabled={busy} onCheckedChange={toggle} data-testid="settings-ai-best-time-toggle" />
      </div>
    </Section>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <PageHeader eyebrow="Tetapan" title="Sambungan & peringatan" />
      {!s ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <div className="grid gap-6 lg:grid-cols-2">
          <PageSection s={s} setS={setS} />
          <ReminderSection s={s} setS={setS} />
          <AiSection s={s} setS={setS} />
          <BrandStyleSection />
          <AutopilotSettingsSection />
          <HelperSettingsSection />
          <DatabaseSettingsSection />
          <BackupSettingsSection />
        </div>
      )}
    </div>
  );
}
